import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as OrganizationIcon,
  Folder as ProjectIcon,
  Assessment as AuditIcon,
  Logout,
  Person,
  KeyboardArrowRight,
  AdminPanelSettings as PortalAdminIcon,
  Settings as ServicesIcon,
  Security as SecurityIcon,
  NewReleases as VersionIcon,
  MailOutline,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import ThemeToggle from './ThemeToggle';

const drawerWidth = 260;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isDark } = useTheme();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Navigation sections - grouped for visual hierarchy
  const navSections = [
    {
      title: 'Overview',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
      ],
    },
    {
      title: 'App Management',
      items: [
        { text: 'Organizations', icon: <OrganizationIcon />, path: '/organizations' },
        { text: 'Projects', icon: <ProjectIcon />, path: '/projects' },
        { text: 'Access Control', icon: <SecurityIcon />, path: '/access-control' },
      ],
    },
    {
      title: 'Portal Management',
      items: [
        { text: 'Portal Admins', icon: <PortalAdminIcon />, path: '/portal-admins' },
      ],
    },
    {
      title: 'System',
      items: [
        { text: 'Services', icon: <ServicesIcon />, path: '/services' },
        { text: 'Versions', icon: <VersionIcon />, path: '/versions' },
        { text: 'Audit Logs', icon: <AuditIcon />, path: '/audit' },
        { text: 'GUS Weekly', icon: <MailOutline />, path: '/gus-weekly' },
      ],
    },
  ];

  // Get current page title
  const getCurrentPageTitle = () => {
    for (const section of navSections) {
      for (const item of section.items) {
        if (item.path === location.pathname) {
          return item.text;
        }
      }
    }
    return 'Dashboard';
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: isDark
          ? 'linear-gradient(180deg, #111111 0%, #0a0a0a 100%)'
          : '#ffffff',
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '10px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isDark ? 'transparent' : 'rgba(0,0,0,0.02)',
          }}
        >
          <img
            src="/icon-silacities.png"
            alt="SilaCities"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.95rem',
              color: isDark ? '#ffffff' : '#0f172a',
              letterSpacing: '-0.3px',
            }}
          >
            SilaCities
          </Typography>
          <Typography
            sx={{
              fontSize: '0.65rem',
              color: '#32828B',
              fontWeight: 600,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
            }}
          >
            Admin Portal
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
        {navSections.map((section, idx) => (
          <Box key={section.title} sx={{ mb: 2 }}>
            <Typography
              sx={{
                px: 3,
                py: 1,
                fontSize: '0.65rem',
                fontWeight: 600,
                color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.4)',
                letterSpacing: '0.8px',
                textTransform: 'uppercase',
              }}
            >
              {section.title}
            </Typography>
            <List disablePadding>
              {section.items.map((item) => {
                const isSelected = location.pathname === item.path;
                return (
                  <ListItem key={item.text} disablePadding sx={{ px: 1.5, py: 0.25 }}>
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => navigate(item.path)}
                      sx={{
                        borderRadius: '8px',
                        py: 1,
                        px: 1.5,
                        transition: 'all 0.15s ease',
                        '&.Mui-selected': {
                          bgcolor: isDark ? 'rgba(50, 130, 139, 0.15)' : 'rgba(50, 130, 139, 0.1)',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(50, 130, 139, 0.2)' : 'rgba(50, 130, 139, 0.15)',
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '3px',
                            height: '60%',
                            bgcolor: '#32828B',
                            borderRadius: '0 2px 2px 0',
                          },
                        },
                        '&:hover': {
                          bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: isSelected
                            ? '#32828B'
                            : isDark
                            ? 'rgba(255,255,255,0.5)'
                            : 'rgba(0,0,0,0.5)',
                          minWidth: 36,
                          '& svg': { fontSize: '1.2rem' },
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected
                            ? (isDark ? '#ffffff' : '#0f172a')
                            : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                        }}
                      />
                      {isSelected && (
                        <KeyboardArrowRight
                          sx={{
                            fontSize: '1rem',
                            color: '#32828B',
                            opacity: 0.7,
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* User Section at Bottom */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        }}
      >
        <Box
          onClick={handleUserMenuOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
            },
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: '#32828B',
              fontSize: '0.9rem',
              fontWeight: 600,
            }}
          >
            {user?.username?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.85rem',
                fontWeight: 500,
                color: isDark ? '#ffffff' : '#0f172a',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.username}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.email}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              border: 'none',
              boxShadow: isDark
                ? '4px 0 24px rgba(0,0,0,0.5)'
                : '4px 0 24px rgba(0,0,0,0.1)',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              border: 'none',
              boxShadow: isDark
                ? '1px 0 0 rgba(255,255,255,0.06)'
                : '1px 0 0 rgba(0,0,0,0.06)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: isDark ? '#0a0a0a' : '#f5f5f7',
          minHeight: '100vh',
          transition: 'background-color 0.2s ease',
        }}
      >
        {/* Top Bar - Minimal */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 4,
            py: 2.5,
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            bgcolor: isDark ? 'rgba(10,10,10,0.8)' : 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(12px)',
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                display: { sm: 'none' },
                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
              }}
            >
              <MenuIcon />
            </IconButton>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: isDark ? '#ffffff' : '#0f172a',
                  letterSpacing: '-0.5px',
                  fontSize: '1.5rem',
                }}
              >
                {getCurrentPageTitle()}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ThemeToggle />
          </Box>
        </Box>

        {/* Page Content */}
        <Box
          sx={{
            flex: 1,
            p: 4,
            overflowY: 'auto',
          }}
        >
          {children}
        </Box>
      </Box>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleUserMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: -1,
            minWidth: 200,
            borderRadius: '12px',
            bgcolor: isDark ? '#1a1a1a' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            boxShadow: isDark
              ? '0 8px 32px rgba(0,0,0,0.5)'
              : '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <MenuItem disabled sx={{ opacity: '0.7 !important' }}>
          <ListItemIcon>
            <Person fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }} />
          </ListItemIcon>
          <Typography sx={{ fontSize: '0.85rem' }}>{user?.email}</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleLogout}
          sx={{
            color: '#ef4444',
            '&:hover': {
              bgcolor: 'rgba(239, 68, 68, 0.1)',
            },
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" sx={{ color: '#ef4444' }} />
          </ListItemIcon>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>Sign Out</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;
